import type { NextPage } from 'next'

import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import PageHeader from '~/components/atoms/PageHeader/PageHeader'
import type { ReportCardProps } from '~/components/blocks/ReportCard/ReportCard'
import ReportsContainer from '~/containers/ReportsContainer/ReportsContainer'
import { reportCard } from '~/schemas/common'
import { onlyPublished, useSanity } from '~/services/sanity'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { createLangStaticPaths } from '~/utils/langPaths'
import { resolvePageSeoMeta } from '~/utils/seoHelper'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

export interface PageData {
  reports: ReportCardProps[]
  featuredReports: ReportCardProps[]
  breadcrumb: BreadcrumbProps['list']
  reportCategories: {
    name: string
    slug: string
  }[]
  bigFeatureIndex?: number
}

const ReportsIndexPage: NextPage<PageData> = ({
  reports,
  featuredReports,
  breadcrumb,
  reportCategories,
  bigFeatureIndex,
}: PageData) => {
  return (
    <>
      <Breadcrumb list={breadcrumb} />
      <PageHeader
        centered
        summary="Explore our data-driven art market reports on blue chip print & edition trends and artist performance in the secondary art market."
        title="Market Reports"
        preventSplitLines
      />
      {featuredReports && featuredReports.length > 0 && (
        <ReportsContainer
          reports={featuredReports}
          title="Featured reports"
          noPaddingBottom
          slider
        />
      )}
      <div id="recent-reports">
        <ReportsContainer
          reports={reports}
          title="Recently added"
          filterCategories={reportCategories}
          bigFeatureIndex={bigFeatureIndex}
        />
      </div>
    </>
  )
}

function getQuery(limiter: string | number, scorer: string | number) {
  return `
    {
      "meta": *[_type == 'page' && slug.current == $slug ${limiter}] ${scorer}[0]{ meta, _id },
      "reports": *[_type == 'report' ${onlyPublished} && featured != true] | order(publishedDate desc) {
        ${reportCard}
      },
      "featuredReports": *[_type == 'report' ${onlyPublished} && featured == true] | order(publishedDate desc) {
        ${reportCard}
      }
    }`
}

const getStaticPropsOriginal = async function getStaticProps(
  context: any,
  localizedData: { settings: any }
) {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { client, limiter, scorer } = await useSanity()

    const lang = (context?.params?.lang as Locale) || 'en'
    const messages = await loadMessages(lang)

    const query = getQuery(limiter, scorer)

    const { meta: metaProps, ...data } = await client.fetch(query, {
      slug: 'market-reports',
      lang: lang,
    })

    // Use centralized SEO helper with locale-aware templates
    await resolvePageSeoMeta(context, metaProps, 'page')

    const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
      ['market-reports'],
      '',
      undefined,
      messages,
      lang
    )

    const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

    let bigFeatureIndex = undefined
    // How to sell prints report is a main feature
    // Manually finding it from the array of reports and putting it purposefully in position 4
    const bigFeatureReportIndex = data?.reports.findIndex(
      (item: ReportCardProps) => {
        return (
          item.slug ===
          'a-guide-to-selling-prints-how-to-sell-in-the-current-market'
        )
      }
    )

    if (data.reports && bigFeatureReportIndex !== -1) {
      const bigFeaturedReport = data.reports?.splice(bigFeatureReportIndex, 1)
      const featurePosition = 3
      data.reports.splice(featurePosition, 0, ...bigFeaturedReport)
      bigFeatureIndex = featurePosition
    }

    const reportCategories = [...data.reports].map(item => item.primaryCategory)
    const featureCategories = [...data.reports].map(
      item => item.primaryCategory
    )
    const combinedCategories = [...reportCategories, ...featureCategories]
    const reportCategoriesUnique = Array.from(
      new Map(combinedCategories.map(item => [item.slug, item])).values()
    )

    return {
      props: {
        smallHeader: true,
        meta: metaProps?.meta || {},
        notFound: false,
        url: '/market-reports',
        ...data,
        bigFeatureIndex,
        reportCategories: reportCategoriesUnique,
        breadcrumb,
        noAnimatedTheme: true,
        breadcrumbJsonLd,
      },
    }
  } catch {
    return { props: { notFound: true } }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)
    if ('notFound' in result || 'redirect' in result) {
      return result // Return notFound or redirect directly
    }
    return { props: await addTranslations(result.props, context) }
  }
)
export const getStaticPaths = async () => createLangStaticPaths()

export default ReportsIndexPage
